/**
 * @file services/companies.js
 * @since v1.0
 * @author Carlos Lenon <code@carloslenon.com>
 */

// #region constant
// #endregion
// #region common
import type { AIEvidenceResetCell } from 'api/models/AIEvidenceResetCell';
import type { AIEvidenceResetRequest } from 'api/models/AIEvidenceResetRequest';
import type { AIEvidenceResetResponse } from 'api/models/AIEvidenceResetResponse';
import type { AIReviewBatchItem } from 'api/models/AIReviewBatchItem';
import type { AIReviewBatchItemResult } from 'api/models/AIReviewBatchItemResult';
import type { AIReviewBatchRequest } from 'api/models/AIReviewBatchRequest';
import type { AIReviewBatchResponse } from 'api/models/AIReviewBatchResponse';
import type { MappingReviewResponse } from 'api/models/MappingReviewResponse';
import type { RestoredMapping } from 'api/models/RestoredMapping';
import type { SecurityCitationCreateRequest } from 'api/models/SecurityCitationCreateRequest';
import type { SecurityCitationCreateResponse } from 'api/models/SecurityCitationCreateResponse';
import type { SecurityMappingCreateResponse } from 'api/models/SecurityMappingCreateResponse';
import { ApiResponse } from 'common/types/response';
import BaseServiceRetryWrapper from './baseRetry';
import type { CitationReferenceData, MappingReferenceData } from './reference-generator/types';
import { CAPTABLE } from '../common/endpoints';
// #endregion

/** One cap-table cell: a saved security and its grid field name; the server resolves other spellings. */
export type ClearCellTarget = AIEvidenceResetCell;

/** A workbook clear's scope; no document means every Excel reference on the cap table. */
export type ClearMappingsScope = Pick<AIEvidenceResetRequest, 'document_id' | 'sheet_name'>;

/** An Excel reference a review or a clear brought back, as the grid reads it, with the security it belongs to. */
export type RestoredMappingReference = MappingReferenceData & Pick<RestoredMapping, 'security_id'>;

/** What a clear retired and brought back, so the page can patch itself without a reload. */
export type ClearEvidenceResult = Omit<AIEvidenceResetResponse, 'restored_mappings'> & {
  restored_mappings: RestoredMappingReference[];
};

/** A manual citation to save on a saved security; `token_references` is every selection the citation ends with. */
export type SecurityCitationCreateBody = Omit<SecurityCitationCreateRequest, 'mode'> & { mode: 'append' | 'replace' };

/** The citation the server kept, adopted or created, as the grid reads it, and the AI citations a Replace dismissed. */
export type SecurityCitationCreateResult = Omit<SecurityCitationCreateResponse, 'citation'> & {
  citation: CitationReferenceData;
};

/** An Excel pick to save on a saved security, with the grid's own cell references. */
export type SecurityMappingCreateBody = Pick<
  MappingReferenceData,
  'field_name' | 'document_reference' | 'sheet_name' | 'cell_references'
>;

/** The cell's Excel reference after the pick, as the grid reads it, and the editable references it replaced. */
export type SecurityMappingCreateResult = Omit<SecurityMappingCreateResponse, 'mapping'> & {
  mapping: MappingReferenceData;
};

/** A mapping review's settled rows and decision times, the references it replaced and the ones it brought back. */
export type MappingReviewOutcome = Omit<MappingReviewResponse, 'restored'> & { restored: RestoredMappingReference[] };

/** One card in a review batch: its security and only the ids that card decides, every list sent. */
export type ReviewBatchItem = Required<AIReviewBatchItem>;

/** A review batch: the action, one item per card, and an optional note the field decisions keep. */
export type ReviewBatchRequest = Omit<AIReviewBatchRequest, 'action' | 'items'> & {
  action: 'accept' | 'decline' | 'reopen';
  items: ReviewBatchItem[];
};

/** One card's outcome, with the rows it brought back as the grid reads them; a failed item changed nothing. */
export type ReviewBatchItemResult = Omit<AIReviewBatchItemResult, 'status' | 'restored'> & {
  status: 'settled' | 'failed';
  restored: RestoredMappingReference[];
};

/** Every card's outcome in request order, plus the AI-created securities the batch deleted or brought back. */
export type ReviewBatchResponse = Omit<AIReviewBatchResponse, 'results'> & { results: ReviewBatchItemResult[] };

class CapatableService extends BaseServiceRetryWrapper {
  /**
   * @function
   * @name dataById
   * @description load captable by id selected.
   * @param {INTEGER} dataId the captable id
   */
  dataById = (dataId: string) => this.get(`${CAPTABLE.byId}/${dataId}`);

  /**
   * @function
   * @name getCaptableById
   * @description load captable by id selected.
   * @param {integer} captableId the captable id
   */
  getCaptableById = <T = any>(captableId: number) =>
    this.get(CAPTABLE.captableById(captableId)) as Promise<ApiResponse<T>>;

  /**
   * @function
   * @name updateCaptableById
   * @description update captable by id selected.
   * @param {INTEGER} captableId the captable id
   */
  updateCaptableById = (captableId: number, data: unknown) => this.put(CAPTABLE.captableById(captableId), data);

  /**
   * @function
   * @name updateSecuritiesById
   * @description update captable by id selected.
   * @param {INTEGER} captableId the captable id
   */
  updateSecuritiesById = (captableId: number, data: unknown) =>
    this.put(CAPTABLE.updateSecurititesById(captableId), data);

  /**
   * @function
   * @name updateFundOwnershipById
   * @description update captable fund ownership by id selected.
   * @param {INTEGER} captableId the captable id
   */
  updateFundOwnershipById = (captableId: number, data: unknown) =>
    this.put(CAPTABLE.updateFundOwnershipById(captableId), data);

  createCapTableProforma = (data: unknown) => this.post(CAPTABLE.createProforma(), data);

  getPrimaryCaptableByCompanyDate = (companyDate: string) =>
    this.get(CAPTABLE.primaryCaptableByCompanyDate(companyDate));

  gotFinalAllocation = (id: number) => this.get(CAPTABLE.gotFinalAllocation(id));

  hasMeasurementDates = (id: number) => this.get(CAPTABLE.hasMeasurementDates(id));

  /**
   * @function
   * @name getAllBreakpoints
   * @description get all breakpoints (waterfall and custom)
   * @param {string} id the captable id
   */
  getAllBreakpointsData = (id: string) => this.get(CAPTABLE.allBreakpointsData(id));

  /**
   * @function
   * @name updateCustomBreakpoints
   * @description update custom breakpoints by cap table id.
   * @param {string} captableId the captable id
   */
  updateCustomBreakpoints = (captableId: string, data: unknown) =>
    this.put(CAPTABLE.allBreakpointsData(captableId), data);

  /**
   * @function
   * @name reviewCitation
   * @description accept, (soft, reversibly) dismiss, or reopen a single citation
   * reference (SCAL-8963/9262) - the citation-level settle for review-panel items
   * that carry no ai_finding field metadata to settle through. 'reopen' undoes
   * either decision back to pending.
   * @param {number} citationId the citation reference id
   * @param {string} action 'accept' | 'dismiss' | 'reopen'
   */
  reviewCitation = (citationId: number, action: 'accept' | 'dismiss' | 'reopen') =>
    this.patch(CAPTABLE.citationReviewById(citationId), { action });

  /**
   * @function
   * @name reviewMappings
   * @description accept, dismiss or reopen AI Excel mapping rows; the response also lists the
   * rows the action retired and carries the rows it restored, so the page patches without a reload.
   * @param {number[]} mappingIds the mapping reference ids to settle
   * @param {string} action 'accept' | 'dismiss' | 'reopen'
   */
  reviewMappings = (mappingIds: number[], action: 'accept' | 'dismiss' | 'reopen') =>
    this.patch<MappingReviewOutcome>(CAPTABLE.mappingReview, { mapping_ids: mappingIds, action });

  /**
   * @function
   * @name reopenAiFields
   * @description reopen settled (accepted/dismissed) AI review fields to pending
   * (SCAL-9262). Un-accept restores the value the accept wrote (previous_value
   * baseline) only while the cell still holds the AI value; restored_fields
   * echoes which requested fields were reverted, so the caller can flag cards
   * whose manual edit was kept instead.
   * @param {number} securityId the security id
   * @param {string[]} fieldNames the field names to reopen
   */
  reopenAiFields = (securityId: number, fieldNames: string[]) =>
    this.patch<{ reopened_fields: string[]; restored_fields: string[]; errors: string[] }>(
      CAPTABLE.reopenAiFieldsById(securityId),
      { field_names: fieldNames }
    );

  /**
   * @function
   * @name reviewBatch
   * @description accept, decline or reopen many review cards in one request, one all-or-nothing item per card;
   * results come back in request order. A 400 means nothing ran (an id the page holds is no longer linked).
   * @param {number} capTableId the cap table id
   * @param {ReviewBatchRequest} request the action, one item per card, and an optional note for field decisions
   */
  reviewBatch = (capTableId: number, request: ReviewBatchRequest) =>
    this.patch<ReviewBatchResponse>(CAPTABLE.aiReviewBatchByCapTableId(capTableId), request);

  /**
   * @function
   * @name clearCapTable
   * @description delete every security on a cap table, with its values, AI
   * suggestions, citations and Excel references. Refused with a 409 when a
   * security is still used by fund ownership or a valuation.
   * @param {number} capTableId the cap table id
   */
  clearCapTable = (capTableId: number) => this.post<{ securities_deleted: number }>(CAPTABLE.clearById(capTableId));

  /**
   * @function
   * @name clearCells
   * @description keep only the values in these cells: retire every citation, Excel reference and AI suggestion on them.
   * @param {number} capTableId the cap table id
   * @param {ClearCellTarget[]} cells the saved cells to clear
   */
  clearCells = (capTableId: number, cells: ClearCellTarget[]) =>
    this.post<ClearEvidenceResult>(CAPTABLE.aiEvidenceResetById(capTableId), { mode: 'clear_cells', cells });

  /**
   * @function
   * @name clearMappings
   * @description retire every Excel reference in the scope, whatever its source or review state.
   * @param {number} capTableId the cap table id
   * @param {ClearMappingsScope} scope the workbook document and optional sheet; empty for the whole cap table
   */
  clearMappings = (capTableId: number, scope: ClearMappingsScope) =>
    this.post<ClearEvidenceResult>(CAPTABLE.aiEvidenceResetById(capTableId), { mode: 'clear_mappings', ...scope });

  /**
   * @function
   * @name createSecurityCitation
   * @description save a manual citation on a saved security at once; the server keeps, adopts or creates the row.
   * @param {number} securityId the security id
   * @param {SecurityCitationCreateBody} body the field, document, every selection the citation ends with, and the mode
   */
  createSecurityCitation = (securityId: number, body: SecurityCitationCreateBody) =>
    this.post<SecurityCitationCreateResult>(CAPTABLE.securityCitationsById(securityId), body);

  /**
   * @function
   * @name createSecurityMapping
   * @description save an Excel pick on a saved security at once; it becomes the cell's one editable reference.
   * @param {number} securityId the security id
   * @param {SecurityMappingCreateBody} body the field, workbook document, sheet and cells
   */
  createSecurityMapping = (securityId: number, body: SecurityMappingCreateBody) =>
    this.post<SecurityMappingCreateResult>(CAPTABLE.securityMappingsById(securityId), body);
}

export default CapatableService;
